import { KP_VAT_RATE } from '../constants/vat'

/**
 * Расчёт «НДС сверху» — одна формула для КП, счёта заказа и шаблона для 1С:УНФ
 * (паспорт docs/features/kp-vat-mode.md). Считаем так же, как УНФ:
 *
 *   цена без НДС  = цена с НДС / 1,22, до копейки;
 *   сумма строки  = цена без НДС × количество, до копейки;
 *   НДС строки    = сумма строки × 22 %, до копейки;
 *   Итого         = Σ сумм строк;  НДС = Σ НДС строк;  Всего к оплате = Итого + НДС.
 *
 * В микрохабе цена позиции хранится с НДС: цена без НДС × 1,22 до 4 знаков
 * (451,84 → 551,2448) — из неё цена без НДС восстанавливается точно.
 */

/** Округление до `digits` знаков, половина — вверх; без ошибки двоичной дроби (1,005 → 1,01). */
function roundTo(value: number, digits: number): number {
  const factor = 10 ** digits
  return Math.round(Number((value * factor).toPrecision(15))) / factor
}

export const roundMoney = (value: number): number => roundTo(value, 2)
const roundPrice4 = (value: number): number => roundTo(value, 4)

/** Цена без НДС из цены с НДС — до копейки. */
export function netPriceFromGross(gross: number, ratePct: number = KP_VAT_RATE): number {
  return roundMoney((gross * 100) / (100 + ratePct))
}

/** Цена с НДС из цены без НДС — до 4 знаков (так хранится цена позиции и строки счёта). */
export function grossPriceFromNet(net: number, ratePct: number = KP_VAT_RATE): number {
  return roundPrice4((net * (100 + ratePct)) / 100)
}

export interface VatOnTopLine {
  /** Сумма строки без НДС. */
  sum: number
  /** НДС строки. */
  vat: number
  /** Сумма строки с НДС. */
  total: number
}

export interface VatOnTopTotals {
  lines: VatOnTopLine[]
  /** Итого без НДС. */
  net: number
  /** НДС. */
  vat: number
  /** Всего к оплате. */
  total: number
}

/** Итоги «НДС сверху» по строкам с ценой БЕЗ НДС. */
export function vatOnTopTotals(
  rows: ReadonlyArray<{ netPrice: number; quantity: number }>,
  ratePct: number = KP_VAT_RATE,
): VatOnTopTotals {
  let net = 0
  let vat = 0
  const lines = rows.map((r) => {
    const sum = roundMoney(r.netPrice * r.quantity)
    const lineVat = roundMoney((sum * ratePct) / 100)
    net = roundMoney(net + sum)
    vat = roundMoney(vat + lineVat)
    return { sum, vat: lineVat, total: roundMoney(sum + lineVat) }
  })
  return { lines, net, vat, total: roundMoney(net + vat) }
}
